import { useState, useEffect } from "react"
import { Calendar } from "@/components/ui/calendar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CalendarDays, CalendarIcon, X } from 'lucide-react'
import { format, isValid, isSameDay, isWithinInterval } from "date-fns"
import { API_ENDPOINTS } from "../../constants/config";

const StudentCalendarModal = ({ attendance = [], showDirectly = false, userClassIds: propUserClassIds }) => {
  const [holidays, setHolidays] = useState([])
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [open, setOpen] = useState(showDirectly)
  const [weekendConfig, setWeekendConfig] = useState({ saturday: "all", sunday: "all" })
  const [userClassIds, setUserClassIds] = useState(() => (Array.isArray(propUserClassIds) ? propUserClassIds : []))

  useEffect(() => {
    fetchHolidays()
  }, [])

  useEffect(() => {
    if (Array.isArray(propUserClassIds)) setUserClassIds(propUserClassIds)
  }, [propUserClassIds])

  useEffect(() => {
    if (showDirectly) {
      setOpen(true)
    }
  }, [showDirectly])

  useEffect(() => {
    const stored = localStorage.getItem("user")
    if (!stored || userClassIds.length > 0) return
    try {
      const user = JSON.parse(stored)
      if (user.role !== "student") return
      fetch(API_ENDPOINTS.usersProfile, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
        .then((res) => res.json())
        .then((data) => {
          const u = data?.data?.user
          const classId = u?.classMapping?.class?._id ?? u?.enrollment?.class?._id ?? u?.classMapping?.class ?? u?.enrollment?.class
          if (classId) setUserClassIds([String(classId)])
        })
        .catch(() => {})
    } catch {}
  }, [userClassIds.length])

  const fetchHolidays = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.holidays, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch holidays")
      }
      const data = await response.json()
      const validHolidays = (data.holidays || []).filter(holiday => {
        if (holiday.type === "vacation") {
          return holiday.dateRange && isValid(new Date(holiday.dateRange.from)) && isValid(new Date(holiday.dateRange.to))
        }
        return isValid(new Date(holiday.date))
      })
      setHolidays(validHolidays)
      setWeekendConfig(data.weekendConfig || { saturday: "all", sunday: "all" })
    } catch (error) {
      console.error("Error fetching holidays:", error)
      setHolidays([])
    }
  }

  const isHolidayApplicableToUser = (holiday) => {
    const applicable = holiday.applicableClasses
    if (!applicable || !applicable.length) return true
    if (!userClassIds.length) return true
    const applicableIds = (applicable || []).map((c) => (c && (c._id || c)) ? String(c._id || c) : null).filter(Boolean)
    return userClassIds.some((id) => applicableIds.includes(String(id)))
  }

  const holidaysForUser = (holidays || []).filter(isHolidayApplicableToUser)

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      if (!isValid(date)) {
        return "Invalid Date"
      }
      return format(date, "MMMM d, yyyy")
    } catch (error) {
      console.error("Error formatting date:", error)
      return "Invalid Date"
    }
  }

  const formatDay = (dateString) => {
    try {
      const date = new Date(dateString)
      if (!isValid(date)) {
        return ""
      }
      return format(date, "EEEE")
    } catch (error) {
      console.error("Error formatting day:", error)
      return ""
    }
  }

  const isWeekend = (date) => {
    const dayOfWeek = date.getDay()
    return dayOfWeek === 0 || dayOfWeek === 6
  }

  const isHoliday = (date) => {
    return holidaysForUser.some((holiday) => {
      if (holiday.type === "vacation" && holiday.dateRange) {
        const fromDate = new Date(holiday.dateRange.from)
        const toDate = new Date(holiday.dateRange.to)
        return isWithinInterval(date, { start: fromDate, end: toDate })
      }
      const holidayDate = new Date(holiday.date)
      return isValid(holidayDate) && isSameDay(holidayDate, date)
    })
  }

  const getHolidayName = (date) => {
    const holiday = holidaysForUser.find((holiday) => {
      if (holiday.type === "vacation" && holiday.dateRange) {
        const fromDate = new Date(holiday.dateRange.from)
        const toDate = new Date(holiday.dateRange.to)
        return isWithinInterval(date, { start: fromDate, end: toDate })
      }
      const holidayDate = new Date(holiday.date)
      return isValid(holidayDate) && isSameDay(holidayDate, date)
    })
    return holiday ? holiday.name : ""
  }

  const getHolidayInfo = () => {
    if (!holidaysForUser || holidaysForUser.length === 0) {
      return []
    }

    const mapped = holidaysForUser.map((holiday) => {
      let sortDate = new Date(0)
      if (holiday.type === "vacation" && holiday.dateRange?.from) {
        sortDate = new Date(holiday.dateRange.from)
      } else if (holiday.date) {
        sortDate = new Date(holiday.date)
      }
      if (holiday.type === "vacation" && holiday.dateRange) {
        return {
          name: holiday.name || "Vacation",
          date: `${formatDate(holiday.dateRange.from)} - ${formatDate(holiday.dateRange.to)}`,
          type: (holiday.type || "VACATION").toUpperCase(),
          _sort: sortDate.getTime()
        }
      }
      if (holiday.date) {
        return {
          name: holiday.name || "Holiday",
          date: formatDate(holiday.date),
          type: (holiday.type || "PUBLIC").toUpperCase(),
          _sort: sortDate.getTime()
        }
      }
      return {
        name: holiday.name || "Unknown Holiday",
        date: "Date not available",
        type: (holiday.type || "OTHER").toUpperCase(),
        _sort: 0
      }
    })
    return mapped.sort((a, b) => a._sort - b._sort)
  }

  const getAttendanceStatus = (date) => {
    if (!attendance || attendance.length === 0) return null
    
    const record = attendance.find((record) => {
      const attendanceDate = new Date(record.date)
      return isValid(attendanceDate) && isSameDay(attendanceDate, date)
    })
    
    if (!record) return null
    
    // Handle student attendance (classAttendance or busAttendance)
    if (record.classAttendance) {
      return record.classAttendance.toLowerCase()
    }
    if (record.busAttendance) {
      return record.busAttendance.toLowerCase()
    }
    
    // Handle teacher attendance (status field)
    if (record.status) {
      return record.status.toLowerCase()
    }
    
    // Handle direct attendance field
    if (record.attendance) {
      return record.attendance.toLowerCase()
    }
    
    return null
  }

  const hasAttendance = (date) => {
    return getAttendanceStatus(date) !== null
  }

  // Custom day renderer to match the design
  const customDayRender = (day, selectedDay, dayProps) => {
    const isSelected = isSameDay(day, selectedDay)
    const isWeekendDay = isWeekend(day)
    const isHolidayDay = isHoliday(day)
    const isSchoolHoliday = isHolidayDay && !isWeekendDay
    const attendanceStatus = getAttendanceStatus(day)
    const hasAtt = hasAttendance(day)

    let className = "w-8 h-8 rounded-full flex items-center justify-center text-sm relative "

    // Priority: Selected > Holiday/Weekend > Attendance
    // If it's a current day that's highlighted in your UI
    if (isSelected) {
      className += "bg-blue-500 text-white border-none "
    }
    // Apply border for weekend days (if not selected)
    else if (isWeekendDay) {
      className += "border border-red-500 text-red-500 "
    } 
    // Apply styles for holidays (if not selected and not weekend)
    else if (isSchoolHoliday) {
      className += "border border-blue-500 text-blue-500 "
    }
    // Apply attendance border if there's attendance and no holiday/weekend
    else if (hasAtt && attendanceStatus) {
      if (attendanceStatus === "present") {
        className += "border border-teal-500 text-teal-500 "
      } else if (attendanceStatus === "absent") {
        className += "border border-black text-black "
      } else if (attendanceStatus === "late") {
        className += "border border-yellow-500 text-yellow-500 "
      }
    }

    return (
      <div {...dayProps} className={className}>
        {format(day, "d")}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" onClick={fetchHolidays}>
          <CalendarIcon className="w-4 h-4" />
          View Calendar
        </Button>
      </DialogTrigger>
      {/* Use the DialogContent without the default close button */}
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
        <div className="flex justify-between items-center mb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Holiday Calendar
            </DialogTitle>
          </DialogHeader>
          {/* Custom close button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="rounded-full" 
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Calendar */}
          <div className="flex flex-col">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              components={{
                Day: ({ date, selected, ...props }) => customDayRender(date, selected, props)
              }}
            />
            
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2">Holiday Types</h3>
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-xs">Weekend</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-xs">School Holiday</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-xs">Public Holiday</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                    <span className="text-xs">Vacation Period</span>
                  </div>
                </div>
              </div>
              
              {attendance && attendance.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Attendance</h3>
                  <div className="grid grid-cols-2 gap-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border border-teal-500"></div>
                      <span className="text-xs">Present</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border border-black"></div>
                      <span className="text-xs">Absent</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border border-yellow-500"></div>
                      <span className="text-xs">Late</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Holiday List */}
          <div className="flex flex-col">
            <h3 className="font-medium mb-4 text-lg">All Holidays</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {getHolidayInfo().length > 0 ? (
                getHolidayInfo().map((holiday, idx) => (
                  <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="font-medium text-sm flex-1">{holiday.name}</h4>
                      <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                        holiday.type === "PUBLIC" ? "bg-green-100 text-green-800" : 
                        holiday.type === "VACATION" ? "bg-purple-100 text-purple-800" : 
                        "bg-blue-100 text-blue-800"
                      }`}>
                        {holiday.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{holiday.date}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No holidays found for this month</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default StudentCalendarModal